// Admin dashboard — displays platform-wide metrics and 30-day growth charts
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@startus/lib';
import { fetchDashboardData } from '../../services/dashboard.service';
import type { AdminMetrics } from '../../types/dashboard';
import { MetricCard } from '../../components/dashboard/MetricCard';
import { DashboardLineChart } from '../../components/dashboard/DashboardLineChart';

const Dashboard: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchDashboardData()
      .then(setMetrics)
      .catch((err: unknown) => {
        captureException(err);
        console.error('[Dashboard] Failed to fetch admin metrics:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div role="alert" className="alert alert-error">
        <span>{t('common.error')}</span>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      {/* Row 1 — Volume metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label={t('admin:admin.dashboard.totalUsers')} value={metrics.totalUsers} />
        <MetricCard
          label={t('admin:admin.dashboard.totalStartups')}
          value={metrics.totalStartups}
        />
        <MetricCard
          label={t('admin:admin.dashboard.totalApplications')}
          value={metrics.totalApplications}
        />
      </div>

      {/* Row 2 — Rate + remaining count metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          label={t('admin:admin.dashboard.totalOpportunities')}
          value={`${metrics.totalOpportunities}`}
        />
        <MetricCard
          label={t('admin:admin.dashboard.activationRate')}
          value={`${metrics.activationRate.toFixed(1)}%`}
        />
        <MetricCard
          label={t('admin:admin.dashboard.startupCompletionRate')}
          value={`${metrics.startupCompletionRate.toFixed(1)}%`}
        />
      </div>

      {/* Row 3 — 30-day growth charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DashboardLineChart
          title={t('admin:admin.dashboard.newUsers30d')}
          data={metrics.newUsersLast30d}
        />
        <DashboardLineChart
          title={t('admin:admin.dashboard.newStartups30d')}
          data={metrics.newStartupsLast30d}
        />
      </div>
    </div>
  );
};

export default Dashboard;
